// React and Router
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

// Icons
import { SiAuthelia } from 'react-icons/si'
import { MdOutlineHome } from 'react-icons/md'
import { RiStackLine } from 'react-icons/ri'
import { AiOutlineProduct } from 'react-icons/ai'
import { HiOutlineUserGroup } from 'react-icons/hi'
import { RxCodesandboxLogo } from 'react-icons/rx'
import { FiFilter } from 'react-icons/fi'
import { BsGrid3X3GapFill } from 'react-icons/bs'
import { FaTags } from "react-icons/fa";

// Assets
import logo from '../../assets/logo/logo.png'

// Utils
import useUserStore from '../../utils/state/useUserStore'

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const user = useUserStore((state) => state.user)
  const location = useLocation()
  const { pathname } = location

  const trigger = useRef(null)
  const sidebar = useRef(null)

  const storedSidebarExpanded = localStorage.getItem('sidebar-expanded')
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true',
  )

  // Wrap setSidebarOpen in useCallback to prevent unnecessary re-renders
  const handleSetSidebarOpen = useCallback((value) => {
    setSidebarOpen(value)
  }, [setSidebarOpen])

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return
      handleSetSidebarOpen(false)
    }
    document.addEventListener('click', clickHandler)
    return () => document.removeEventListener('click', clickHandler)
  }, [sidebarOpen, handleSetSidebarOpen]) // Added missing dependency

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return
      handleSetSidebarOpen(false)
    }
    document.addEventListener('keydown', keyHandler)
    return () => document.removeEventListener('keydown', keyHandler)
  }, [sidebarOpen, handleSetSidebarOpen]) // Added missing dependency

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded.toString())
    if (sidebarExpanded) {
      document.querySelector('body')?.classList.add('sidebar-expanded')
    } else {
      document.querySelector('body')?.classList.remove('sidebar-expanded')
    }
  }, [sidebarExpanded])

  // If setSidebarExpanded is not used, you can remove it or use it
  // For example, you could add a toggle function:
  const toggleSidebarExpanded = () => {
    setSidebarExpanded(!sidebarExpanded)
  }

  return (
    <aside
      ref={sidebar}
      className={`absolute lg:static left-0 top-0 z-[99] flex h-screen w-[300px] flex-col border-r shadow-md overflow-y-hidden duration-300 ease-linear lg:translate-x-0 bg-white ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
    >
      <div className='flex items-center justify-center gap-2 px-5 py-4'>
        <NavLink to='/'>
          <img
            src={logo}
            className='h-10 invert'
            alt='Logo' // Added meaningful alt text
          />
        </NavLink>
        <button
          ref={trigger}
          onClick={() => handleSetSidebarOpen(!sidebarOpen)}
          aria-controls='sidebar'
          aria-expanded={sidebarOpen}
          className='hidden'
        ></button>
      </div>
      <div className='no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear px-5 lg:px-5'>
        <nav className='border-t-2'>
          <div className='pt-4 pb-4'>
            <ul className='flex flex-col gap-3 text-black'>
              <li>
                <NavLink
                  to='/'
                  onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                  className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname === '/' && '!bg-prime !text-white'
                    }`}
                >
                  <MdOutlineHome className='text-xl' />
                  Home
                </NavLink>
              </li>
              {user?.includes('pattern:read') ? (
                <li>
                  <NavLink
                    to='/pattern'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/pattern') && '!bg-prime !text-white'
                      }`}
                  >
                    <RxCodesandboxLogo className='text-xl' />
                    Pattern
                  </NavLink>
                </li>
              ) : null}
              {user?.includes('brand:read') ? (
                <li>
                  <NavLink
                    to='/brand'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/brand') && '!bg-prime !text-white'
                      }`}
                  >
                    <FaTags className='text-xl' />
                    Brand
                  </NavLink>
                </li>
              ) : null}
              {user?.includes('material:read') ? (
                <li>
                  <NavLink
                    to='/material'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/material') && '!bg-prime !text-white'
                      }`}
                  >
                    <RiStackLine className='text-xl' />
                    Material
                  </NavLink>
                </li>
              ) : null}
              {user?.includes('joint:read') ? (
                <li>
                  <NavLink
                    to='/joint'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/joint') && '!bg-prime !text-white'
                      }`}
                  >
                    <AiOutlineProduct className='text-xl' />
                    Joint
                  </NavLink>
                </li>
              ) : null}

              {/* Seamless Texture - Debug version */}
              {console.log('Rendering Seamless Texture check:', {
                hasPermission: user?.includes('seamless-texture:read'),
                userPermissions: user
              })}
              {user?.includes('seamless-texture:read') ? (
                <li>
                  <NavLink
                    to='/seamless-texture'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/seamless-texture') && '!bg-prime !text-white'
                      }`}
                  >
                    <BsGrid3X3GapFill className='text-xl' />
                    Seamless Texture
                  </NavLink>
                </li>
              ) : (
                // Optional: Show a message in console if permission is missing
                console.log('Seamless Texture hidden - permission missing')
              )}

              {user?.includes('limit:read') ? (
                <li>
                  <NavLink
                    to='/limit'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/limit') && '!bg-prime !text-white'
                      }`}
                  >
                    <FiFilter className='text-xl' />
                    Limit
                  </NavLink>
                </li>
              ) : null}
              {user?.includes('user:read') ? (
                <li>
                  <NavLink
                    to='/user'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/user') && '!bg-prime !text-white'
                      }`}
                  >
                    <HiOutlineUserGroup className='text-xl' />
                    User
                  </NavLink>
                </li>
              ) : null}
              {user?.includes('role:read') ? (
                <li>
                  <NavLink
                    to='/role'
                    onClick={() => handleSetSidebarOpen(!sidebarOpen)}
                    className={`group relative flex items-center gap-2.5 rounded-lg font-medium duration-300 ease-in-out border border-orange hover:text-white hover:bg-prime py-2 px-4 ${pathname.includes('/role') && '!bg-prime !text-white'
                      }`}
                  >
                    <SiAuthelia className='text-xl' />
                    Role
                  </NavLink>
                </li>
              ) : null}
            </ul>
          </div>
        </nav>
      </div>
    </aside>
  )
}

export default Sidebar